import { getTeamComponentFromPath } from './getTeamComponentFromPath'

export interface SentryBeforeSendOptions {
  /** Team map used to derive the `team` and `component` tags from the current URL. */
  team: 'cp' | 'crm' | 'sourcing'
  /**
   * Drop events whose original exception looks like an HTTP 401: a `status`, `statusCode` or
   * `response.status` of 401, or a message containing the characters `401`.
   */
  ignoreHttp401?: boolean
}

type Primitive = string | number | boolean | bigint | symbol | null | undefined

/**
 * The slice of a Sentry event the hook reads and writes. Each workspace resolves its own copy of
 * the Sentry types, so the hook is typed structurally and the caller's event type flows through.
 */
export interface SentryBeforeSendEvent {
  breadcrumbs?: { category?: string; message?: string }[]
  fingerprint?: string[]
  tags?: { [key: string]: Primitive }
}

export interface SentryBeforeSendHint {
  originalException?: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const readString = (value: unknown, key: string) => {
  if (!isRecord(value)) return undefined
  const field = value[key]
  return typeof field === 'string' ? field : undefined
}

const readField = (value: unknown, key: string) => (isRecord(value) ? value[key] : undefined)

const hasGraphqlErrorBreadcrumb = (event: SentryBeforeSendEvent) =>
  Boolean(
    event.breadcrumbs?.some(
      (breadcrumb) =>
        breadcrumb.category === 'sentry.event' && breadcrumb.message && /GraphqlError:/i.test(breadcrumb.message),
    ),
  )

const isHttp401 = (error: unknown) =>
  isRecord(error) &&
  (readField(error, 'status') === 401 ||
    readField(error, 'statusCode') === 401 ||
    readField(error.response, 'status') === 401 ||
    Boolean(readString(error, 'message')?.includes('401')))

/**
 * Builds the `beforeSend` hook shared by the apps. It drops events the error links already
 * report, fingerprints Luther exceptions on their message so the same failure groups together,
 * and tags every event with the team and component owning the current URL.
 */
export const createSentryBeforeSend =
  ({ team, ignoreHttp401 = false }: SentryBeforeSendOptions) =>
  <E extends SentryBeforeSendEvent>(event: E, hint?: SentryBeforeSendHint): E | null => {
    const error = hint?.originalException

    if (readString(error, 'name') === 'ApolloError') return null

    // GraphQL failures are captured by graphqlErrorLink, so events carrying one are duplicates.
    if (hasGraphqlErrorBreadcrumb(event)) return null

    // Auth 401s mean the session has ended; they are not application errors.
    if (ignoreHttp401 && isHttp401(error)) return null

    const lutherMessage = readString(error, 'name') === 'LutherException' ? readString(error, 'message') : undefined
    const teamTags = getTeamComponentFromPath(window.location.pathname, team)

    return Object.assign({}, event, lutherMessage === undefined ? {} : { fingerprint: [lutherMessage] }, {
      tags: { ...event.tags, team: teamTags.team, component: teamTags.component },
    })
  }
